import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useTranslation } from 'react-i18next'
import { Device, PairedRuuviTag, RuuviTag } from '../../types'
import { getPairedRuuviTags, invokeAction } from '../../services/deviceService'
import { Alert, AlertColor, AlertTitle, Box, Button, Stack, Grid } from '@mui/material'
import { BluetoothSearching } from '@mui/icons-material'
import RuuviTagQrCodeButton from './RuuviTagQrCodeButton'
import LoadingState from '../LoadingState'
import RuuviTagMacAddressInput from './RuuviTagMacAddressInput'
import RuuviTagScannerList from './RuuviTagScannerList'
import ValveInstallRuuviTagInstructions from '../ValveInstallRuuviTagInstructions'
import { DeviceType } from '../../enums'
import {
  createOrUpdateRuuviTag,
  getRuuviTag,
  getRuuviTagAccelerationNow,
  getRuuviTagConditionNow
} from '../../services/ruuviTagService'
import { useDispatch } from 'react-redux'
import {
  createErrorNotification,
  createInfoNotification,
  createSuccessNotification
} from '../../redux/slices/notifications'
import { LoadingButton } from '@mui/lab'
import RuuviTagInstallationInfoAlert from '../RuuviTagInstallationInfoAlert'
import { usePolicy } from '../../hooks'
import { isValve } from '../../utils/device'

const MAC_ADDRESS_LENGTH = 12

export interface RuuviTagScannerProps {
  device: Device
  onChange: (ruuviTag: RuuviTag) => void
  onCancel: () => void
  onFound?: (ruuviTag: RuuviTag) => void
  afterLoad?: (ruuviTag: RuuviTag) => void
  afterCreate?: (ruuviTag: RuuviTag) => void
  isDiscoveryEnabled?: boolean
}

function RuuviTagScanner({
  device,
  onFound,
  onChange,
  onCancel,
  afterLoad,
  afterCreate,
  isDiscoveryEnabled = true
}: RuuviTagScannerProps) {
  const { t } = useTranslation('installation')

  const dispatch = useDispatch()
  const { canCreateRuuviTag } = usePolicy()
  const userCanCreateRuuviTag = canCreateRuuviTag()
  const discoveryEnabled = isDiscoveryEnabled

  const abortControllerRef = useRef<AbortController | null>(null)

  const intervalRef = useRef<NodeJS.Timeout | null>(null)

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const macSourceRef = useRef<'scan' | 'manual' | null>(null)

  const [isScanning, setScanning] = useState<boolean>(false)

  const [isScanCompleted, setScanCompleted] = useState<boolean>(false)

  const [isLoading, setLoading] = useState<boolean>(false)

  const [foundRuuviTags, setFoundRuuviTags] = useState<PairedRuuviTag[]>([])

  const [macAddress, setMacAddress] = useState<string>('')
  const [notFoundAlertSeverity, setNotFoundAlertSeverity] = useState<AlertColor>('error')
  const [isRefreshingOrientation, setRefreshingOrientation] = useState<boolean>(false)
  const [loadedRuuviTag, setLoadedRuuviTag] = useState<RuuviTag | null>(null)
  const [notFoundMessage, setNotFoundMessage] = useState<string | null>(null)
  const [pendingCreateMac, setPendingCreateMac] = useState<string | null>(null)
  const [isCreatingRuuviTag, setCreatingRuuviTag] = useState<boolean>(false)
  // We rely on paired tag response to include latest readings; no extra condition fetch.
  // const [fetchingTemp] = useState<Record<string, boolean>>({})

  function applyLoadedTag(tag: RuuviTag) {
    setNotFoundMessage(null)
    setLoadedRuuviTag(tag)
    afterLoad?.(tag)
    onChange(tag)
  }

  async function loadRuuviTag(mac: string, controller: AbortController) {
    try {
      setLoading(true)
      setLoadedRuuviTag(null)
      setNotFoundMessage(null)
      setNotFoundAlertSeverity('error')
      setPendingCreateMac(null)
      setCreatingRuuviTag(false)

      const tag = await getRuuviTag(mac, { signal: controller.signal })

      applyLoadedTag(tag)
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.code === 'ERR_CANCELED') {
          return
        }

        if (error.response?.status === 404) {
          const source = macSourceRef.current

          if (userCanCreateRuuviTag && device?.name) {
            setPendingCreateMac(mac)
            setNotFoundAlertSeverity('info')
            setNotFoundMessage(t('ruuviTagCreatePromptMessage', { mac: mac.toUpperCase() }))
            return
          }

          if (source === 'scan' && !userCanCreateRuuviTag) {
            setNotFoundAlertSeverity('info')
            setNotFoundMessage(t('ruuviTagPowerOnReminder'))
            dispatch(createInfoNotification(t('ruuviTagPowerOnReminder')))
            return
          }

          setNotFoundAlertSeverity('error')
          setNotFoundMessage(t('ruuviTagWasNotFoundByMacAddress'))
          return
        }

        setNotFoundMessage(null)
        dispatch(createErrorNotification(t('somethingWentWrong')))
        return
      }

      setNotFoundMessage(null)
      dispatch(createErrorNotification(t('somethingWentWrong')))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (macAddress.length !== MAC_ADDRESS_LENGTH) {
      setLoadedRuuviTag(null)
      setNotFoundMessage(null)
      setLoading(false)
      return undefined
    }

    const controller = new AbortController()

    loadRuuviTag(macAddress, controller)

    return () => controller.abort()
  }, [macAddress])

  function handleTagUpdate(tag: PairedRuuviTag) {
    setFoundRuuviTags((prev) => {
      const index = prev.findIndex((existing) => existing.id === tag.id)

      if (index === -1) {
        return [...prev, tag]
      }
      const next = [...prev]
      next[index] = {
        ...next[index],
        ...tag,
        latest: tag.latest ?? next[index].latest
      }

      return next
    })
  }

  // When scanning flag turns on, start the scan loop; clean up on stop/unmount
  useEffect(() => {
    if (!isScanning) return

    if (!discoveryEnabled) {
      setScanning(false)
      return
    }

    const controller = new AbortController()
    abortControllerRef.current = controller
    setScanCompleted(false)
    scanRuuviTags(controller).catch(() => {})
    return () => {
      try {
        controller.abort()
      } catch {
        /* ignore fetch errors within polling loop */
      }
      if (intervalRef.current) clearInterval(intervalRef.current)
      if (timeoutRef.current) clearTimeout(timeoutRef.current)
    }
  }, [isScanning, discoveryEnabled])

  async function scanRuuviTags(controller?: AbortController) {
    if (!discoveryEnabled) {
      return
    }

    if (!isScanning) {
      setScanning(true)
    }

    setFoundRuuviTags([])

    // Invoke action for the device.
    await invokeAction(
      device.name,
      {
        ruuvi_pairing: 60
      },
      {
        signal: controller?.signal
      }
    )

    // Poll for the paired ruuvi tags every 2 seconds.
    const TWO_SECONDS = 2 * 1000
    const interval = setInterval(async () => {
      try {
        const tags = await getPairedRuuviTags(device.name, { signal: controller?.signal })
        if (Array.isArray(tags)) {
          setFoundRuuviTags((prev) => {
            if (tags.length === 0) return prev

            const prevMap = new Map(prev.map((tag) => [tag.id, tag]))

            const next = (tags as PairedRuuviTag[]).map((incoming) => {
              const existing = prevMap.get(incoming.id)

              if (!existing) {
                onFound?.(incoming)
                return incoming
              }

              const previousProjectId = existing.asset?.projectId ?? null
              const incomingProjectId = incoming.asset?.projectId ?? null

              if (previousProjectId === null && incomingProjectId !== null) {
                return {
                  ...existing,
                  latest: incoming.latest ?? existing.latest
                }
              }

              return {
                ...incoming,
                latest: incoming.latest ?? existing.latest
              }
            })

            return next
          })
        }
      } catch (_) {
        // Do nothing if failed.
      }
    }, TWO_SECONDS)
    intervalRef.current = interval

    // Clear the interval after one minute.
    const ONE_MINUTE = 60 * 1000
    const timeout = setTimeout(() => {
      setScanning(false)
      setScanCompleted(true)
      clearInterval(interval)
    }, ONE_MINUTE)
    timeoutRef.current = timeout
  }
  // No extra fetchConditionsForTag; sensor-backend should provide latest condition when pairing.

  function handleManualMacChange(value: string) {
    macSourceRef.current = 'manual'
    setMacAddress(value)
  }

  function handleQrMacDetected(value: string) {
    macSourceRef.current = 'scan'
    setMacAddress(value)
  }

  function startScan() {
    if (isScanning || !discoveryEnabled) return
    setScanCompleted(false)
    setScanning(true)
  }

  function handleScanAgain() {
    if (!discoveryEnabled) return
    startScan()
  }

  async function handleRefreshOrientation() {
    if (isRefreshingOrientation || isScanning || foundRuuviTags.length === 0) {
      return
    }

    setRefreshingOrientation(true)
    try {
      const updatedTags = await Promise.all(
        foundRuuviTags.map(async (tag) => {
          const [conditionResult, accelerationResult] = await Promise.allSettled([
            getRuuviTagConditionNow(tag.id),
            getRuuviTagAccelerationNow(tag.id)
          ])

          const updatedTag: PairedRuuviTag = {
            ...tag,
            latest: {
              ...tag.latest,
              condition:
                conditionResult.status === 'fulfilled'
                  ? conditionResult.value
                  : tag.latest?.condition,
              acceleration:
                accelerationResult.status === 'fulfilled'
                  ? accelerationResult.value
                  : tag.latest?.acceleration
            }
          }

          return updatedTag
        })
      )

      setFoundRuuviTags(updatedTags)
    } catch (_error) {
      dispatch(createErrorNotification(t('somethingWentWrong')))
    } finally {
      setRefreshingOrientation(false)
    }
  }

  function handleStartScan() {
    startScan()
  }

  function handleCancelScan() {
    if (abortControllerRef.current) {
      try {
        abortControllerRef.current.abort()
      } catch {
        /* ignore abort errors */
      }
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    setScanning(false)
    setScanCompleted(false)
  }

  async function handleConfirmCreate() {
    if (!pendingCreateMac || !device?.name) {
      return
    }

    setCreatingRuuviTag(true)
    try {
      const createdTag = await createOrUpdateRuuviTag({
        id: pendingCreateMac,
        device: device.name
      })

      applyLoadedTag(createdTag)
      afterCreate?.(createdTag)
      dispatch(createSuccessNotification(t('createRuuviTagSuccess')))
      setNotFoundMessage(null)
      setPendingCreateMac(null)
    } catch (creationError) {
      if (axios.isCancel(creationError)) {
        return
      }

      setNotFoundAlertSeverity('error')
      setNotFoundMessage(t('createRuuviTagError'))
      dispatch(createErrorNotification(t('createRuuviTagError')))
      setPendingCreateMac(null)
    } finally {
      setCreatingRuuviTag(false)
    }
  }

  function handleCancelCreate() {
    setPendingCreateMac(null)
    setCreatingRuuviTag(false)
    setNotFoundAlertSeverity('error')
    setNotFoundMessage(t('ruuviTagWasNotFoundByMacAddress'))
  }

  return (
    <>
      <Stack direction="column" gap={2}>
        {isValve(device) ? (
          <ValveInstallRuuviTagInstructions valve={device} />
        ) : (
          <RuuviTagInstallationInfoAlert />
        )}

        {!discoveryEnabled && (
          <Alert severity="info">
            <AlertTitle sx={{ m: 0 }}>
              {t('ruuviTagDiscoveryDisabledWhileDeviceOffline')}
            </AlertTitle>
          </Alert>
        )}

        {isScanning ? (
          <>
            <LoadingState
              title={
                <Stack direction="row" gap={1} alignItems="center">
                  <BluetoothSearching fontSize="small" />
                  <span>{t('searchingRuuviTags')}</span>
                </Stack>
              }
              action={
                <Button variant="outlined" color="inherit" onClick={handleCancelScan}>
                  {t('cancel')}
                </Button>
              }
            />

            {foundRuuviTags.length > 0 && (
              <RuuviTagScannerList
                ruuviTags={foundRuuviTags}
                onSelect={onChange}
                onExitInstallation={onCancel}
                onTagUpdate={handleTagUpdate}
              />
            )}
          </>
        ) : isScanCompleted ? (
          <>
            {foundRuuviTags.length === 0 ? (
              <Alert severity="warning">
                <AlertTitle sx={{ m: 0 }}>{t('ruuviTagsWereNotFound')}</AlertTitle>
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                    disabled={!discoveryEnabled}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
                    {t('cancel')}
                  </Button>
                </Stack>
              </Alert>
            ) : (
              <Alert severity="info">
                <AlertTitle sx={{ m: 0 }}>{t('scanCompleted')}</AlertTitle>
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                    disabled={!discoveryEnabled}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <LoadingButton
                    variant="outlined"
                    color="inherit"
                    size="small"
                    loading={isRefreshingOrientation}
                    disabled={isRefreshingOrientation}
                    onClick={handleRefreshOrientation}
                  >
                    {t('refreshOrientation')}
                  </LoadingButton>
                  <Button variant="outlined" color="inherit" size="small" onClick={onCancel}>
                    {t('cancel')}
                  </Button>
                </Stack>
              </Alert>
            )}

            {foundRuuviTags.length > 0 && (
              <RuuviTagScannerList
                ruuviTags={foundRuuviTags}
                onSelect={onChange}
                onExitInstallation={onCancel}
              />
            )}
          </>
        ) : (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="primary"
                  sx={{ mt: 1 }}
                  onClick={handleStartScan}
                  disabled={!discoveryEnabled}
                >
                  {t('startScan')}
                </Button>
              </Grid>
            </Grid>

            {foundRuuviTags.length > 0 && (
              <>
                <RuuviTagScannerList
                  ruuviTags={foundRuuviTags}
                  onSelect={onChange}
                  onExitInstallation={onCancel}
                />
                <Stack direction="row" gap={1} sx={{ mt: 2 }} flexWrap="wrap">
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleScanAgain}
                    disabled={!discoveryEnabled}
                  >
                    {t('scanAgainRefreshOrientations')}
                  </Button>
                  <LoadingButton
                    variant="outlined"
                    color="inherit"
                    size="small"
                    loading={isRefreshingOrientation}
                    onClick={handleRefreshOrientation}
                    disabled={isRefreshingOrientation}
                  >
                    {t('refreshOrientation')}
                  </LoadingButton>
                </Stack>
              </>
            )}
          </>
        )}

        {/* Manual MAC input and create prompt */}
        <Box sx={{ mt: 2 }}>
          <RuuviTagMacAddressInput
            disabled={isLoading}
            onChange={handleManualMacChange}
            value={macAddress}
          />

          <Stack direction="row" justifyContent="center" sx={{ mt: 2 }}>
            <RuuviTagQrCodeButton disabled={isLoading} onMacDetected={handleQrMacDetected} />
          </Stack>

          {notFoundMessage && (
            <Alert severity={notFoundAlertSeverity} sx={{ mt: 2 }}>
              <AlertTitle sx={{ m: 0 }}>{notFoundMessage}</AlertTitle>
              {pendingCreateMac && (
                <Stack
                  direction="row"
                  gap={1}
                  sx={{ mt: 2 }}
                  justifyContent="flex-end"
                  flexWrap="wrap"
                >
                  <Button
                    variant="outlined"
                    color="inherit"
                    size="small"
                    onClick={handleCancelCreate}
                  >
                    {t('actions:cancel')}
                  </Button>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={handleConfirmCreate}
                    loading={isCreatingRuuviTag}
                  >
                    {t('ruuviTagCreatePromptConfirm')}
                  </LoadingButton>
                </Stack>
              )}
            </Alert>
          )}

          {loadedRuuviTag && (
            <Alert severity="success" sx={{ mt: 2 }}>
              <AlertTitle sx={{ m: 0 }}>{t('ruuviTagWasFound')}</AlertTitle>
              {loadedRuuviTag.asset?.name ?? macAddress.toUpperCase()}
            </Alert>
          )}
        </Box>
      </Stack>
    </>
  )
}

export default RuuviTagScanner
